<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import { useLlmCredentialStore } from 'dashboard/store/captain/llmCredentials';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import ModelSelector from './components/ModelSelector.vue';
import FeatureToggle from './components/FeatureToggle.vue';
import LlmCredentials from './components/LlmCredentials.vue';
import CaptainAiStatus from './components/CaptainAiStatus.vue';

const { t } = useI18n();
const store = useStore();

const captainConfigStore = useCaptainConfigStore();
const credentialStore = useLlmCredentialStore();
const { uiFlags } = storeToRefs(captainConfigStore);

const isLoading = computed(() => uiFlags.value.isFetching);

// Audio transcription picks its model and LLM on its own page, Integrations → Audio transcription.
const modelFeatures = computed(() => [
  {
    key: 'editor',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.EDITOR.DESCRIPTION'),
  },
  {
    key: 'assistant',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.ASSISTANT.DESCRIPTION'),
  },
  {
    key: 'copilot',
    title: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.TITLE'),
    description: t('CAPTAIN_SETTINGS.MODEL_CONFIG.COPILOT.DESCRIPTION'),
  },
]);

const featureToggles = ['label_suggestion', 'help_center_search'];

const llmCredentialsRef = ref(null);

// A feature's model or LLM decides which LLM pays for it, and each LLM lists what it serves.
const updatePreferences = async preferences => {
  try {
    await captainConfigStore.updatePreferences(preferences);
    // The reply box reads its switches (label suggestions) from the current account.
    store.dispatch('accounts/get');
    credentialStore.fetch();
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    captainConfigStore.fetch();
  }
};

const handleFeatureToggle = ({ feature, enabled }) =>
  updatePreferences({ captain_features: { [feature]: enabled } });

const handleModelChange = ({ feature, model }) =>
  updatePreferences({ captain_models: { [feature]: model } });

const handleCredentialChange = ({ feature, credential }) =>
  updatePreferences({ captain_credentials: { [feature]: credential } });

onMounted(() => {
  captainConfigStore.fetch();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('CAPTAIN_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t('CAPTAIN_SETTINGS.TITLE')"
        :description="t('CAPTAIN_SETTINGS.DESCRIPTION')"
        icon-name="captain"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-1">
        <CaptainAiStatus
          :features="modelFeatures"
          class="mb-4"
          @add="llmCredentialsRef.openForm()"
        />

        <!-- Every AI call goes out on one of these, so they come first. -->
        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.TITLE')"
          :description="t('CAPTAIN_SETTINGS.LLM_CREDENTIALS.DESCRIPTION')"
        >
          <LlmCredentials ref="llmCredentialsRef" />
        </SectionLayout>

        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.MODEL_CONFIG.TITLE')"
          :description="t('CAPTAIN_SETTINGS.MODEL_CONFIG.DESCRIPTION')"
          with-border
        >
          <div class="grid gap-4">
            <ModelSelector
              v-for="feature in modelFeatures"
              :key="feature.key"
              :feature-key="feature.key"
              :title="feature.title"
              :description="feature.description"
              @change="handleModelChange"
              @credential-change="handleCredentialChange"
            />
          </div>
        </SectionLayout>

        <SectionLayout
          :title="t('CAPTAIN_SETTINGS.FEATURES.TITLE')"
          :description="t('CAPTAIN_SETTINGS.FEATURES.DESCRIPTION')"
          with-border
        >
          <div class="grid gap-4">
            <FeatureToggle
              v-for="feature in featureToggles"
              :key="feature"
              is-allowed
              :feature-key="feature"
              @change="handleFeatureToggle"
              @model-change="handleModelChange"
            />
          </div>
        </SectionLayout>
      </div>
    </template>
  </SettingsLayout>
</template>
